import { confirmDialog } from 'primereact/confirmdialog';
import { getTabulatorLayoutForStandardMask } from './tabulatorLayoutUtils';
import { safeParse } from './tabulatorProgressiveLoading';
import { uiT } from './uiTranslate';

/** Technické stĺpce — nie v dialógu „Nastaviť masku“. */
export const MAINTENANCE_MASK_DIALOG_SKIP_FIELDS = Object.freeze(
  new Set(['rowSelection', 'editor', 'status_indicator', 'contractor_id'])
);

/** V mask dialógu musí zostať stĺpec ID vždy viditeľný (nezávisle od uloženej masky). */
export function ensureMandatoryIdVisible(visibility, defaultVisibility) {
  if (!visibility || typeof visibility !== 'object') return visibility;
  if (defaultVisibility && Object.prototype.hasOwnProperty.call(defaultVisibility, 'id')) {
    return { ...visibility, id: true };
  }
  return visibility;
}

export function mergeColumnVisibilityFromSaved(defaultVisibility, saved) {
  const modernMask = saved && typeof saved === 'object' && Object.prototype.hasOwnProperty.call(saved, 'id');
  const merged = {};

  for (const field of Object.keys(defaultVisibility)) {
    if (saved && typeof saved === 'object' && Object.prototype.hasOwnProperty.call(saved, field)) {
      merged[field] = saved[field] !== false;
    } else if (modernMask) {
      merged[field] = false;
    } else {
      merged[field] = defaultVisibility[field];
    }
  }

  if (!Object.values(merged).some(Boolean)) {
    return ensureMandatoryIdVisible({ ...defaultVisibility }, defaultVisibility);
  }
  return ensureMandatoryIdVisible(merged, defaultVisibility);
}

export function normalizeColumnVisibilityForSave(defaultVisibility, stateObj) {
  const out = {};
  for (const field of Object.keys(defaultVisibility)) {
    out[field] = stateObj?.[field] !== false;
  }
  return ensureMandatoryIdVisible(out, defaultVisibility);
}

export function ensureMaskDialogState(defaultVisibility, partial) {
  const out = {};
  for (const field of Object.keys(defaultVisibility)) {
    if (!Object.prototype.hasOwnProperty.call(partial, field) || partial[field] === undefined || partial[field] === null) {
      out[field] = defaultVisibility[field];
    } else {
      out[field] = partial[field] !== false;
    }
  }
  if (!Object.values(out).some(Boolean)) {
    return ensureMandatoryIdVisible({ ...defaultVisibility }, defaultVisibility);
  }
  return ensureMandatoryIdVisible(out, defaultVisibility);
}

export function columnsFromLayoutForStandardMaskSave(defaultVisibility, raw) {
  const out = {};
  for (const field of Object.keys(defaultVisibility)) {
    out[field] = raw?.[field] !== false;
  }
  return out;
}

export function countDataColumnsVisible(defaultVisibility, vis) {
  if (!vis || typeof vis !== 'object') return 0;
  return Object.keys(defaultVisibility).filter((k) => vis[k] !== false).length;
}

export function clearHeaderFilterForField(table, field) {
  if (!table || field == null) return;
  try {
    table.setHeaderFilterValue?.(field, '', true);
  } catch (_) {}
}

export function applyDataColumnVisibilityToTable(table, defaultVisibility, visibilityMap) {
  if (!table) return;
  for (const f of Object.keys(defaultVisibility)) {
    if (visibilityMap[f] === false) {
      clearHeaderFilterForField(table, f);
      table.hideColumn?.(f);
    } else {
      table.showColumn?.(f);
    }
  }
  setTimeout(() => table.redraw?.(true), 0);
}

export function hasApplicableStandardMaskRow(standardMaskFilters, maskPage, mergeVisibilityFromSaved) {
  const row = (standardMaskFilters || []).find((m) => m.page === maskPage);
  if (!row) return false;
  const attributes = safeParse(row.attributes, []);
  if (!Array.isArray(attributes) || attributes.length <= 1) return false;
  const cols = safeParse(row.columns, {});
  const merged = mergeVisibilityFromSaved(cols && typeof cols === 'object' ? cols : {});
  return Object.values(merged).some(Boolean);
}

/**
 * Podpoložky menu Akcie (mntn001 referencia).
 */
export function buildMaintenanceMaskAkcieSubItems({
  maskPage,
  getTable,
  setUserMaskFilter,
  cleanSorters,
  captureHScroll,
  refreshFromStart,
  closeMenu,
  openMaskDialog,
  openChosenNotificationPreferences,
  hasApplicableStandardMask,
  confirmApplyStandardLayoutToUserMask,
  itemRenderer,
}) {
  const refresh = typeof refreshFromStart === 'function' ? refreshFromStart : () => {};
  const capture = typeof captureHScroll === 'function' ? captureHScroll : () => {};
  const close = typeof closeMenu === 'function' ? closeMenu : () => {};

  return [
    {
      key: '0_1',
      label: uiT('Common.SetFilter'),
      icon: 'pi pi-filter',
      command: () => {
        setUserMaskFilter(maskPage, 'set_filters', null, getTable()?.getHeaderFilters?.() ?? [], null);
        capture();
        refresh();
        close();
      },
      template: itemRenderer,
    },
    {
      key: '0_2',
      label: uiT('Common.ClearFilter'),
      icon: 'pi pi-filter-slash',
      command: () => {
        setUserMaskFilter(maskPage, 'set_filters', null, [], null);
        getTable()?.clearHeaderFilter?.();
        capture();
        refresh();
        close();
      },
      template: itemRenderer,
    },
    {
      key: '0_3',
      label: uiT('Common.SetSorting'),
      icon: 'pi pi-sort-alt',
      command: () => {
        setUserMaskFilter(maskPage, 'set_sorters', null, null, null, cleanSorters(getTable()?.getSorters?.() ?? []));
        capture();
        refresh();
        close();
      },
      template: itemRenderer,
    },
    {
      key: '0_4',
      label: uiT('Common.ClearSorting'),
      icon: 'pi pi-sort-alt-slash',
      command: () => {
        setUserMaskFilter(maskPage, 'set_sorters', null, null, null, []);
        getTable()?.setSort?.([]);
        capture();
        refresh();
        close();
      },
      template: itemRenderer,
    },
    {
      key: '0_5',
      label: uiT('Common.SetMask'),
      icon: 'pi pi-server',
      command: openMaskDialog,
      template: itemRenderer,
    },
    ...(hasApplicableStandardMask
      ? [
          {
            key: '0_5s',
            label: uiT('Common.StandardMask'),
            icon: 'pi pi-clone',
            command: confirmApplyStandardLayoutToUserMask,
            template: itemRenderer,
          },
        ]
      : []),
    {
      key: '0_6',
      label: uiT('Common.Notifications'),
      icon: 'pi pi-envelope',
      command: () => {
        openChosenNotificationPreferences(maskPage);
        close();
      },
      template: itemRenderer,
    },
  ];
}

export function createConfirmSaveStandardMask({
  maskPage,
  getTable,
  setStandardMaskFilter,
  defaultColumnVisibility,
  toast,
}) {
  return () => {
    confirmDialog({
      message: uiT('Common.SaveStandardMaskMessage'),
      header: uiT('Common.SaveStandardMask'),
      icon: 'pi pi-info-circle',
      acceptLabel: uiT('Common.Save'),
      rejectLabel: uiT('Common.Cancel'),
      accept: () => {
        const table = getTable();
        const layout = getTabulatorLayoutForStandardMask(table);
        if (!layout) {
          toast?.current?.show({
            severity: 'warn',
            summary: uiT('Common.Mask'),
            detail: uiT('Common.TableNotReady'),
            life: 4000,
          });
          return;
        }
        const columnsForSave = columnsFromLayoutForStandardMaskSave(defaultColumnVisibility, layout.columns);
        if (!Object.values(columnsForSave).some(Boolean)) {
          toast?.current?.show({
            severity: 'warn',
            summary: uiT('Common.StandardMask'),
            detail: uiT('Common.AtLeastOneColumnVisible'),
            life: 4000,
          });
          return;
        }
        setStandardMaskFilter(maskPage, columnsForSave, layout.attributes);
      },
    });
  };
}

/**
 * Kopírovanie standard masky do user masky (mntn001).
 * @param {object} opts
 * @param {() => void} [opts.onAppliedToTable] — po uložení (layout + visibility + refresh)
 */
export function createConfirmApplyStandardLayoutToUserMask({
  maskPage,
  state,
  setUserMaskFilter,
  defaultColumnVisibility,
  mergeVisibilityFromSaved,
  toast,
  closeMenu,
  onAppliedToTable,
}) {
  return () => {
    if (typeof closeMenu === 'function') closeMenu();
    const standard = (state.standard_mask_filters || []).find((m) => m.page === maskPage);
    if (!standard) {
      toast?.current?.show({
        severity: 'warn',
        summary: uiT('Common.StandardMask'),
        detail: uiT('Common.StandardMaskMissing'),
        life: 4000,
      });
      return;
    }
    const attributes = safeParse(standard.attributes, []);
    if (!Array.isArray(attributes) || attributes.length <= 1) {
      toast?.current?.show({
        severity: 'warn',
        summary: uiT('Common.StandardMask'),
        detail: uiT('Common.StandardMaskInvalidLayout'),
        life: 5000,
      });
      return;
    }
    const jsonColumns = safeParse(standard.columns, {});
    const mergedCols = mergeVisibilityFromSaved(jsonColumns && typeof jsonColumns === 'object' ? jsonColumns : {});
    const colsForSave = normalizeColumnVisibilityForSave(defaultColumnVisibility, mergedCols);
    if (!Object.values(colsForSave).some(Boolean)) {
      toast?.current?.show({
        severity: 'warn',
        summary: uiT('Common.StandardMask'),
        detail: uiT('Common.StandardMaskNoVisibleColumn'),
        life: 4000,
      });
      return;
    }
    confirmDialog({
      message: uiT('Common.ApplyStandardMaskMessage'),
      header: uiT('Common.StandardMask'),
      icon: 'pi pi-info-circle',
      acceptLabel: uiT('Common.Apply'),
      rejectLabel: uiT('Common.Cancel'),
      accept: () => {
        setUserMaskFilter(maskPage, 'all', colsForSave, null, attributes, null, () => {
          if (typeof onAppliedToTable === 'function') {
            onAppliedToTable({ colsForSave, attributes });
          }
        });
      },
    });
  };
}

export function createSaveUserMaskColumns({
  maskPage,
  getTable,
  setUserMaskFilter,
  defaultColumnVisibility,
  columnsVisible,
  setColumnsVisible,
  setMaskVisible,
  toast,
}) {
  return () => {
    const normalized = normalizeColumnVisibilityForSave(defaultColumnVisibility, columnsVisible);
    const anyVisible = Object.values(normalized).some(Boolean);
    if (!anyVisible) {
      toast?.current?.show({ severity: 'warn', summary: uiT('Common.Mask'), detail: uiT('Common.SelectAtLeastOneColumn'), life: 4000 });
      return;
    }
    const filtersSnapshot = getTable()?.getHeaderFilters?.() ?? [];
    setUserMaskFilter(maskPage, 'all', normalized, filtersSnapshot, null, null);
    setColumnsVisible(normalized);
    setMaskVisible(false);
  };
}

export function createOpenMaskDialog({
  columnsVisible,
  setColumnsVisible,
  defaultColumnVisibility,
  mergeVisibilityFromSaved,
  setMaskVisible,
  closeMenu,
}) {
  return () => {
    const merged = mergeVisibilityFromSaved(columnsVisible);
    setColumnsVisible(ensureMaskDialogState(defaultColumnVisibility, merged));
    setMaskVisible(true);
    if (typeof closeMenu === 'function') closeMenu();
  };
}

export function createMaskSetFieldHandler({
  defaultColumnVisibility,
  columnsVisible,
  setColumnsVisible,
  getTable,
  applyVisibilityToTable,
  toast,
}) {
  return (field, value) => {
    if (!Object.prototype.hasOwnProperty.call(defaultColumnVisibility, field)) return;
    const table = getTable();
    const next = { ...defaultColumnVisibility, ...columnsVisible, [field]: value };
    if (value === false && countDataColumnsVisible(defaultColumnVisibility, next) < 1) {
      toast?.current?.show({
        severity: 'warn',
        summary: uiT('Common.Mask'),
        detail: uiT('Common.AtLeastOneColumnVisible'),
        life: 3500,
      });
      return;
    }
    if (typeof applyVisibilityToTable === 'function') {
      applyVisibilityToTable(table, next);
    } else {
      applyDataColumnVisibilityToTable(table, defaultColumnVisibility, next);
    }
    setColumnsVisible(next);
  };
}

export function createMaskSelectAllHandler({
  defaultColumnVisibility,
  setColumnsVisible,
  getTable,
  applyVisibilityToTable,
}) {
  return () => {
    const next = { ...defaultColumnVisibility };
    for (const k of Object.keys(next)) next[k] = true;
    const table = getTable();
    if (typeof applyVisibilityToTable === 'function') {
      applyVisibilityToTable(table, next);
    } else {
      applyDataColumnVisibilityToTable(table, defaultColumnVisibility, next);
    }
    setColumnsVisible(next);
  };
}

export function createMaskDeselectAllHandler({
  defaultColumnVisibility,
  setColumnsVisible,
  getTable,
  applyVisibilityToTable,
}) {
  return () => {
    const next = {};
    for (const k of Object.keys(defaultColumnVisibility)) next[k] = false;
    if (Object.prototype.hasOwnProperty.call(defaultColumnVisibility, 'id')) {
      next.id = true;
    }
    const table = getTable();
    if (typeof applyVisibilityToTable === 'function') {
      applyVisibilityToTable(table, next);
    } else {
      applyDataColumnVisibilityToTable(table, defaultColumnVisibility, next);
    }
    setColumnsVisible(next);
  };
}
